import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UserCheckIcon, HomeIcon, ScaleIcon, CheckCircle2Icon, CalendarIcon, UsersIcon, BookOpenIcon, MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const courses = [{
  icon: MapIcon,
  title: 'Living in non-Muslim countries',
  body: 'A practical roadmap for relocating and thriving in non-Muslim countries — country choice, legal essentials, housing, faith life, and raising confident Muslim children.',
  to: '#living-in-non-muslim'
}, {
  icon: UserCheckIcon,
  title: 'Identity & Values',
  body: 'Build a resilient personal identity by embodying Islamic ethical values into clear, confident decisions for modern life.',
  to: '#muslim-life'
}, {
  icon: HomeIcon,
  title: 'Family Life',
  body: 'Strengthen marriage, parenting, and family dynamics — adapted to non-Muslim societies.',
  to: '#family-marriage'
}, {
  icon: ScaleIcon,
  title: 'Professional Ethics',
  body: 'Apply Islamic principles to workplace decisions, leadership, and career growth.',
  to: '#professional-business'
}];
const courseFeatures = [{
  icon: CalendarIcon,
  title: '8 week cohorts',
  body: 'Focused, time-bounded format that respects busy adult schedules.'
}, {
  icon: UsersIcon,
  title: 'Focused group learning',
  body: 'Discussion-based cohorts with peers and experienced facilitators.'
}, {
  icon: BookOpenIcon,
  title: 'Applied learning',
  body: 'Practical, contextual content — not theory. Every session leads to action.'
}];
// Course prices and tuition amounts are temporarily hidden. Set to true to restore them.
const SHOW_PRICES = false;
export function CoreCourses() {
  return <>
      <SubpageHero eyebrow="Practical guidance for everyday decisions in 8 week cohorts" title="Core Courses" paragraphs={['Our most applied courses for Muslim adults living in non-Muslim countries.', 'Identity, values, family life, integration, and professional ethics — practical, contextual, grounded in Islamic principles, applied to contemporary contexts.']} primaryCta={{
      label: 'Explore Catalog',
      to: '#catalog'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/1KJnu5Mw1PZbQZTNxTbn3x/CoreCourses3.jpg" imageAlt="Muslim woman studying with a laptop and notebook on a city balcony overlooking a tree-lined street" compact masked data-id="element-1687" data-mp="3d6jec26rrefo" />

      {/* What are Core Courses */}
      <section id="catalog" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-1688" data-mp="ng5z4cvy5zg3">
        <Container data-id="element-1689" data-mp="27z01dpitwjxe">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1690" data-mp="281m8xp5pyzfm">
            What are Core Courses?
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1691" data-mp="1z6oci83d54jj">
            Core programs are the foundation of our adult direction. They focus
            on the most relevant areas of life: living confidently in non-Muslim
            societies, family and personal responsibilities, and ethical
            professional conduct.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-1692" data-mp="1k8vzbmlags8i">
            {courses.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px',
            ease: [0.22, 1, 0.36, 1]
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-1693" data-mp="1g8vk3zqd8d0e" data-mpi={i}>
                <Link to={c.to} className={`group block h-full rounded-2xl border border-ink-900/5 p-6 shadow-soft transition-shadow hover:shadow-card ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i % 4]}`} data-id="element-1694" data-mp="1y9zopnlj1jz5">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-1695" data-mp="1alzx6lge5pji">
                    <c.icon size={18} strokeWidth={1.6} data-id="element-1696" data-mp="1enbf04t0x6h5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1697" data-mp="10nsk4dp83rms">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1698" data-mp="15ogcb2p6ueil">
                    {c.body}
                  </p>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1699" data-mp="h3v40fph1ih9">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1700" data-mp="qzew0r0wzx0q">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1701" data-mp="zzbbq35btljn">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Living in Non-Muslim countries */}
      <section id="living-in-non-muslim" className="py-16 md:py-24 scroll-mt-24 bg-white/60" data-id="element-1702" data-mp="1nqgw8vm7p20a">
        <Container data-id="element-1703" data-mp="1jm956rsa79yw">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1704" data-mp="1qrsckdnm90tt">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1705" data-mp="1vrofsaohoo8b">
              <div data-id="element-1706" data-mp="20nvb79j9ehc0">
                <div className="flex items-center gap-3 mb-3" data-id="element-1707" data-mp="25g6feol18pad">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1708" data-mp="25ij3ljhdvvvv">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1709" data-mp="2ej7fo8n7vm64">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1710" data-mp="2ofk87370ztza">
                  Living in non-Muslim countries: Essentials for Muslim Families
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1711" data-mp="u2axx96no4fz">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-1712" data-mp="37cksjdnha9vu">
                  €290{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1713" data-mp="32jrokrzu35kt">
                    / course
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-1714" data-mp="2tk4ms536zxr8">
                  <Button variant="primary" data-id="element-1715" data-mp="2tl79e0thfe1c">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1716" data-mp="3q9lyb0ouwe0u">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1717" data-mp="3m9l7shohgcdf">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1718" data-mp="3c9kogvqm5w98">
                  In 8 weeks, build a clear, confident plan for relocating and
                  thriving in non-Muslim countries without compromising your family's values.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1719" data-mp="4s2orjl8h94a">
                  A practical roadmap for navigating legal, financial, and
                  practical essentials without overwhelm, while raising a strong
                  Muslim family and integrating with confidence.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1720" data-mp="3w0q1jb5ero08">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1721" data-mp="nik2agxhvy52">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1722" data-mp="dt0jfb9s84a7">
                    {['Families planning to relocate', 'New arrivals in non-Muslim countries', 'Parents seeking guidance', 'Professionals moving abroad'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1723" data-mp="1b9g1hz4a5vmc">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1724" data-mp="30ql14n09sy4z">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1725" data-mp="30rmonqyh3d3l">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1726" data-mp="281w8lcda614u">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Live weekly sessions'
                  }, {
                    icon: MapIcon,
                    text: 'Practical Q&A'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Peer support group'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1727" data-mp="2dt6a3k0uua2b">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1728" data-mp="28v4rf07cccrd">
                          <f.icon size={14} strokeWidth={2} data-id="element-1729" data-mp="23wl4awh0guxf" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1730" data-mp="1yvnrz0iomv3u">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1731" data-mp="1pxbymqqflj7p">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1732" data-mp="3p7tms94x2qmg">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1733" data-mp="3k87aq30trgf8">
                    {['Clear relocation roadmap', 'Understanding of legal rights', 'Housing & finance basics', 'Schooling & community plan'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1734" data-mp="actaw24shxey">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1735" data-mp="38wt6oab90935" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1736" data-mp="1248uroeyp61h">
                <div className="px-7 md:px-10 py-10" data-id="element-1737" data-mp="122nxvs56db0g">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1738" data-mp="2z4mw0vqmlgqp">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1739" data-mp="385tehacu51ml">
                    {[{
                    num: '1',
                    title: 'Choosing Your Country',
                    desc: 'Community, cost, infrastructure'
                  }, {
                    num: '2',
                    title: 'Legal & Visa Essentials',
                    desc: 'Pathways, documents, family reunification'
                  }, {
                    num: '3',
                    title: 'Housing & Finances',
                    desc: 'Rental contracts, banking, halal investing, with referral to qualified scholars and certified institutions'
                  }, {
                    num: '4',
                    title: 'Career & Employment Rights',
                    desc: 'Job search, contracts, workplace religious rights, hiring, and fair termination'
                  }, {
                    num: '5',
                    title: 'Education & Academic Pathways',
                    desc: 'School systems, university admission, selection criteria, and enrollment processes'
                  }, {
                    num: '6',
                    title: 'Faith & Community Life',
                    desc: 'Mosques, halal food, Ramadan, hijab rights'
                  }, {
                    num: '7',
                    title: 'Education for Children',
                    desc: 'Academic institutions, Islamic identity, language'
                  }, {
                    num: '8',
                    title: 'Identity & Integration',
                    desc: 'Raising confident Muslim children in non-Muslim countries'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1740" data-mp="3d5z9k8f12mfr">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1741" data-mp="387xmlcehe2z0">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1742" data-mp="3vvpa7c5h22fl">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1743" data-mp="3qzi1xcplseuh">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1744" data-mp="2fkblcasq7afb">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1745" data-mp="2adwok2co9smz">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1746" data-mp="2k2oe63u9g0uq">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1747" data-mp="2k513x369ffic">
                        €290
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1748" data-mp="2t33arzcod2dj">/ course</span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1749" data-mp="2y3h8angngtxb">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1750" data-mp="332c0uh4f5wum">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1751" data-mp="13oy7qqfo50hx" />
                        <span data-id="element-1752" data-mp="13r155vj5hvwo">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1753" data-mp="18pvnyxh4bkgs">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1754" data-mp="10nbuy7ysyqx6" />
                        <span data-id="element-1755" data-mp="vp1v3k24v258">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1756" data-mp="1alj6uce8iisb">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1757" data-mp="15ln56jt0f3fx" />
                        <span data-id="element-1758" data-mp="1emuqm1afg9sl">Practical resources & templates</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1759" data-mp="1tcabx1r6vn0w">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1760" data-mp="1tdlfp1kmwus0">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Muslim Life & Integration */}
      <section id="muslim-life" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-[#F7EDE9]/60" data-id="element-1761" data-mp="2lxz36k492v7o">
        <PatternDecor side="right" y="top" size="md" data-id="element-1762" data-mp="27xxyetkv73mx" />
        <Container data-id="element-1763" data-mp="2cx15skobhly2">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1764" data-mp="2cwhnh2ur8vji">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1765" data-mp="1y7bqllje3hes">
              <div data-id="element-1766" data-mp="1p8hurxdvuv2a">
                <div className="flex items-center gap-3 mb-3" data-id="element-1767" data-mp="1p99i6kxbtmhh">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1768" data-mp="1t9a6qye53e29">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1769" data-mp="1k9es2jldl0xh">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1770" data-mp="1zq3mkkczir8t">
                  Muslim Life & Integration
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1771" data-mp="1qpf6lk416gta">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-1772" data-mp="1lrwujkupvlh2">
                  €200{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1773" data-mp="1gsagj972e177">
                    / course
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-1774" data-mp="1727fqmtpbiyb">
                  <Button variant="primary" data-id="element-1775" data-mp="173941szqa1xz">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1776" data-mp="132gu0xj17t0n">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1777" data-mp="y0rfv90iiz8c">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1778" data-mp="34oqet08fcdvk">
                  In 8 weeks, learn to live calmly and confidently in non-Muslim
                  societies, maintaining your identity without conflict or
                  isolation.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1779" data-mp="3ipueraw60u21">
                  Understand practical Shariah boundaries in everyday
                  environments and navigate social conduct, boundaries, and
                  respect with clarity.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1780" data-mp="2uyfbnfwke8pq">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1781" data-mp="2zqoy2lyh0qn9">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1782" data-mp="238kb0n39awlr">
                    {['Muslim adults in the non-Muslim countries', 'Young professionals', 'Parents', 'Community members'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1783" data-mp="1jq5ed57i5qsb">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1784" data-mp="zxz3ubr7gx9z">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1785" data-mp="14ymqkl8eirye">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1786" data-mp="19ry1oyk8xuw4">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Interactive discussions'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Applied fiqh scenarios'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Expert facilitation'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1787" data-mp="3mye6rh8j7fz8">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1788" data-mp="3i4jngewu930o">
                          <f.icon size={14} strokeWidth={2} data-id="element-1789" data-mp="387ykdz4dq1zh" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1790" data-mp="3463gcmv2dai0">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1791" data-mp="2pjmljpms6wbq">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1792" data-mp="2tkzq7we50th2">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1793" data-mp="2fmi8qgetsd7l">
                    {['Confidence in daily interactions', 'Practical Shariah boundaries', 'Tools for respectful integration', 'Stronger sense of identity'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1794" data-mp="3db1qzr9uttr7">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1795" data-mp="33hup4gebovvv" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1796" data-mp="1fl4t2flof7r9">
                <div className="px-7 md:px-10 py-10" data-id="element-1797" data-mp="1td1gi6cegthb">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1798" data-mp="1tembfx2mmefp">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1799" data-mp="wmisd1u2ninv">
                    {[{
                    num: '1',
                    title: 'Islamic Values & Daily Life',
                    desc: 'Core principles in practice'
                  }, {
                    num: '2',
                    title: 'Family Life & Responsibilities',
                    desc: 'Roles and relationships'
                  }, {
                    num: '3',
                    title: 'Fiqh for Everyday Life',
                    desc: 'Accessible and practical — no prior study required'
                  }, {
                    num: '4',
                    title: 'Adab & Social Conduct',
                    desc: 'Manners and interactions'
                  }, {
                    num: '5',
                    title: 'Understanding Western Culture',
                    desc: 'Cultural norms, societal values, and local context'
                  }, {
                    num: '6',
                    title: 'Living Islam in a Secular Society',
                    desc: 'Navigating modern challenges'
                  }, {
                    num: '7',
                    title: 'Discrimination & Legal Rights',
                    desc: 'Handling Islamophobia, workplace bias, and reporting mechanisms'
                  }, {
                    num: '8',
                    title: 'Legacy & Future Planning',
                    desc: 'Preserving faith across generations and long-term stability'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1800" data-mp="2r24rx1z8u10l">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1801" data-mp="2r3ftqw94oyp2">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1802" data-mp="2cc780y8cjxh7">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1803" data-mp="3514d1znjrxs0">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1804" data-mp="175p7ouh2lxir">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1805" data-mp="tc7yo3m0g36z">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1806" data-mp="jf38lsp1nnkw">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1807" data-mp="of7ujy8q1aii">
                        €200
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1808" data-mp="agrehgoyy7np">/ course</span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1809" data-mp="24th7zxbfdeuk">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1810" data-mp="24tzc1g4bwxd1">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1811" data-mp="29qzl7zwh6g8d" />
                        <span data-id="element-1812" data-mp="2jvfzioub3pa2">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1813" data-mp="2exo3bimmph02">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1814" data-mp="2soav0qtz26mn" />
                        <span data-id="element-1815" data-mp="2sqdtlz96x82z">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1816" data-mp="2nvq1zriw3xk6">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1817" data-mp="32l5r732rvuxr" />
                        <span data-id="element-1818" data-mp="3bmdp3k1dmtrb">Applied fiqh scenarios</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1819" data-mp="37n4ru574odr0">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1820" data-mp="1h8umhvshokz5">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Family, Marriage & Personal Life */}
      <section id="family-marriage" className="py-16 md:py-24 scroll-mt-24 bg-sand-50/60" data-id="element-1821" data-mp="2bjtsyguzcpus">
        <Container data-id="element-1822" data-mp="2fjc9l0frqert">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1823" data-mp="2flyh502nsua1">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1824" data-mp="1ns66cb4r40yb">
              <div data-id="element-1825" data-mp="1rwno64rjw46g">
                <div className="flex items-center gap-3 mb-3" data-id="element-1826" data-mp="1wtmj2pj1gf5p">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1827" data-mp="1wv78y1uer9x8">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1828" data-mp="386yfifcgxvhu">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1829" data-mp="3dd4xurgr3zwv">
                  Family, Marriage & Personal Life
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1830" data-mp="38kk5zvhenwvz">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-1831" data-mp="34jiclva66f1x">
                  €220{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1832" data-mp="2zjvt543r02ko">
                    / course
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-1833" data-mp="2poklt6uo5xmy">
                  <Button variant="primary" data-id="element-1834" data-mp="2lmgwpff2uuj7">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1835" data-mp="nwz5kuc88sht">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1836" data-mp="juucpxtlrzw8">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1837" data-mp="155wssxlk86gv">
                  In 8 weeks, strengthen your relationships and navigate modern
                  family challenges with applied Islamic guidance and emotional
                  intelligence.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1838" data-mp="q8nzhycp4smi">
                  Support for adults in family and personal life, focusing on
                  strengthening relationships with respect, balance, and
                  responsibility.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1839" data-mp="v77ibpvztoan">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1840" data-mp="v6fnwie4twly">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1841" data-mp="26lukhl2fimgj">
                    {['Married couples', 'Parents', 'Individuals seeking marriage', 'Family counselors'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1842" data-mp="3fkmc0r0usoif">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1843" data-mp="2e41ap3gk1yj">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1844" data-mp="2d2gvu0jy345">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1845" data-mp="v1q2jqp7qui8">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Safe discussion space'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Practical exercises'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Scenario-based learning'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1846" data-mp="ly2y0srcbv3b">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1847" data-mp="gzhw8owfx3cr">
                          <f.icon size={14} strokeWidth={2} data-id="element-1848" data-mp="c2hq6dzlc0ln" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1849" data-mp="70k1v5cc2flu">{f.text}</span>
                      </div>)}
                  </div>
                  <p className="mt-5 rounded-xl border border-sage-200 bg-sage-50 px-4 py-3 text-sm leading-relaxed text-ink-700" data-id="element-1850" data-mp="2enlqmu5axl8x">
                    To ensure the utmost comfort and privacy, sensitive and
                    scenario-based discussions are conducted in strictly
                    gender-separated groups, guided by professional moderators.
                  </p>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1851" data-mp="1v0nufxqypipz">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1852" data-mp="1zzqx5bjgzcus">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1853" data-mp="24vpggkpxy4oc">
                    {['Stronger family dynamics', 'Better communication skills', 'Understanding of rights & duties', 'Healthy boundaries'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1854" data-mp="3ixdef06c3ge8">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1855" data-mp="3exmtve7ehwi5" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1856" data-mp="1j8aes6fnn8y">
                <div className="px-7 md:px-10 py-10" data-id="element-1857" data-mp="ffdfsbmo4nda">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1858" data-mp="ahk79bxw7h9u">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1859" data-mp="2c9gislk28r4m">
                    {[{
                    num: '1',
                    title: 'Marriage & Family in Islam',
                    desc: 'Applied rights & responsibilities'
                  }, {
                    num: '2',
                    title: 'Islamic Family Law',
                    desc: 'Practical overview (non-legal)'
                  }, {
                    num: '3',
                    title: 'Civil Marriage & Divorce Laws',
                    desc: 'Legal registration, prenuptial agreements, and Western dissolution frameworks'
                  }, {
                    num: '4',
                    title: 'Inheritance & Asset Division',
                    desc: 'Estate planning, wealth distribution, and property rights under Western law'
                  }, {
                    num: '5',
                    title: 'Raising Children',
                    desc: 'Parenting, school, identity in non-Muslim countries'
                  }, {
                    num: '6',
                    title: 'Emotional Intelligence',
                    desc: 'Communication and relationships'
                  }, {
                    num: '7',
                    title: 'Conflict Resolution',
                    desc: 'Navigating modern family challenges calmly and constructively'
                  }, {
                    num: '8',
                    title: 'Personal Growth & Well-being',
                    desc: 'Maintaining individual and spiritual balance within the family'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1860" data-mp="8unr4nh9d7ze">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1861" data-mp="8tcj2bgs5e2h">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1862" data-mp="q2x3eb7e0m7x">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1863" data-mp="z4xyp2j28y6y">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1864" data-mp="18upug02wbxd0">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1865" data-mp="1dsreooc262q2">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1866" data-mp="1mvacgu0zlp5g">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1867" data-mp="284xftt4rbje8">
                        €220
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1868" data-mp="2352sfzk6xbxx">/ course</span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1869" data-mp="2i10t9v5274gh">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1870" data-mp="2d5lixd69fqot">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1871" data-mp="1pdwh967jtpv7" />
                        <span data-id="element-1872" data-mp="1kfuykme1bsk9">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1873" data-mp="1z491syfd5j1c">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1874" data-mp="1z6c4oj1m76nh" />
                        <span data-id="element-1875" data-mp="1uchk7de6qnnc">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1876" data-mp="16mn8x09il757">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1877" data-mp="11o256qv205c0" />
                        <span data-id="element-1878" data-mp="3i3p10yiajt83">Safe discussion space</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1879" data-mp="33fc5g2n48ce4">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1880" data-mp="38ddqgt43qls8">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Professional & Business Programs */}
      <section id="professional-business" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-white/60" data-id="element-1881" data-mp="3r2u1wbhbrynf">
        <PatternDecor side="left" y="middle" size="md" data-id="element-1882" data-mp="2bphgw8730qqi" />
        <Container data-id="element-1883" data-mp="2fphu5pig2vvz">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1884" data-mp="2fq1ch7c0bmaj">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1885" data-mp="tuktpkinrv4c">
              <div data-id="element-1886" data-mp="g4hgfj1rbbr1">
                <div className="flex items-center gap-3 mb-3" data-id="element-1887" data-mp="g139rym28gds">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1888" data-mp="l3c4azekzqi0">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1889" data-mp="1cs5k4w12n1r2">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1890" data-mp="13ss7juhnhx24">
                  Professional & Business Programs
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1891" data-mp="3jilfo0i9vdbk">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-1892" data-mp="3okavrukcqh6i">
                  €320{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1893" data-mp="3gxnigonja17w">
                    / course
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-1894" data-mp="3q0ex0xbphd3r">
                  <Button variant="primary" data-id="element-1895" data-mp="3pzdi2hrkk6gr">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1896" data-mp="2xbhfiwfbjkc2">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1897" data-mp="2t9w2nb2sb61v">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1898" data-mp="372ltbst9cz5b">
                  In 8 weeks, master ethical decision-making, cross-cultural
                  communication, and responsible business practices for the
                  modern workplace.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1899" data-mp="29ju5rra6upx0">
                  Designed for entrepreneurs, specialists, and managers focusing
                  on ethics, trust, and responsibility in professional life.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1900" data-mp="n9d970ui03j2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1901" data-mp="s7et1nzr06vl">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1902" data-mp="3qrerk3xlcwnh">
                    {['Entrepreneurs', 'Managers & leaders', 'Specialists', 'Business owners'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1903" data-mp="8l0cg1nl6gni">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1904" data-mp="30g36ldraw7v1">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1905" data-mp="2vobcwuculm0o">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1906" data-mp="2qp87h8syhds6">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Case studies'
                  }, {
                    icon: UsersIcon,
                    text: 'Professional networking'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Negotiation practice'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1907" data-mp="2yuj0pgm4wxaq">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1908" data-mp="33rj7jtqt642w">
                          <f.icon size={14} strokeWidth={2} data-id="element-1909" data-mp="2pve2ycied7zm" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1910" data-mp="2uu8uq3yce6vv">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1911" data-mp="2kvt5k6ivl7p0">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1912" data-mp="270h0b573fytu">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1913" data-mp="1x445g3ztbexi">
                    {['Ethical business framework', 'Stronger negotiation skills', 'Cross-cultural competence', 'Practical finance basics'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1914" data-mp="1djnh5h07pvr5">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1915" data-mp="1nh1omr45ra7b" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1916" data-mp="266zssw4u4j5o">
                <div className="px-7 md:px-10 py-10" data-id="element-1917" data-mp="269m2b1bad8qj">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1918" data-mp="2k7u53pv0749m">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1919" data-mp="2nagpz9xogevb">
                    {[{
                    num: '1',
                    title: 'Islamic Ethics in Professional Life',
                    desc: 'Ethics at work and in business'
                  }, {
                    num: '2',
                    title: 'Business Registration & Structure',
                    desc: 'Legal frameworks, company incorporation, and Western compliance'
                  }, {
                    num: '3',
                    title: 'Taxation & Financial Regulation',
                    desc: 'Tax systems, corporate reporting, and lawful optimization'
                  }, {
                    num: '4',
                    title: 'Islamic Finance (Practical Basics)',
                    desc: 'Understanding, not certification'
                  }, {
                    num: '5',
                    title: 'Business in Diverse Societies',
                    desc: 'Negotiations, partnerships, contracts'
                  }, {
                    num: '6',
                    title: 'Cross-Cultural Communication',
                    desc: 'Effective communication across cultures'
                  }, {
                    num: '7',
                    title: 'Negotiation & Conflict Resolution',
                    desc: 'Practical tools for conflict'
                  }, {
                    num: '8',
                    title: 'Leadership & Growth',
                    desc: 'Scaling businesses with high ethical and professional standards'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1920" data-mp="11965okpf6u5a">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1921" data-mp="168ipsxowpn46">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1922" data-mp="wcfg96rq6m77">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1923" data-mp="1oxp3g6aufoj4">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1924" data-mp="1f85m552s42qd">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1925" data-mp="27phzk9l7lpv2">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1926" data-mp="3se2huvfhc7r2">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1927" data-mp="3nfsmajpuf54x">
                        €320
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1928" data-mp="5zj676hw36oa">/ course</span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1929" data-mp="39gj81pl76l3l">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1930" data-mp="39j5c3f8vqih2">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1931" data-mp="34jsq0wpu1ffj" />
                        <span data-id="element-1932" data-mp="3iishec4qc8fe">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1933" data-mp="3ef2stmo21qud">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1934" data-mp="xqywbainnbj9" />
                        <span data-id="element-1935" data-mp="tpwwargwyvg8">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1936" data-mp="ts9bwxz78tq4">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1937" data-mp="18dspslgjtlfd" />
                        <span data-id="element-1938" data-mp="yf3c74voqn4x">Professional networking</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1939" data-mp="th1qsdasebqa">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1940" data-mp="1r1xq1ixzyo9f">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How courses work */}
      <section className="py-16 md:py-24 bg-[#EDF2F7]/60" data-id="element-1941" data-mp="29u8p11pj6229">
        <Container data-id="element-1942" data-mp="2530chwswm0kv">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1943" data-mp="251pc86yo3ayi">
            How Core Courses work
          </h2>
          <p className="mt-5 text-ink-700 max-w-3xl leading-relaxed" data-id="element-1944" data-mp="2dd7l0x7x082f">
            Short, focused, and applied — designed for adults who want clarity
            without academic overhead.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1945" data-mp="2me5oneefydkh">
            {courseFeatures.map((f, i) => {
            const tones = ['bg-white', 'bg-[#EDF2F7]', 'bg-[#F7EDE9]'];
            return <motion.div key={f.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-1946" data-mp="2w860s0854j2p" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1947" data-mp="2pdnnhe6zcrkw">
                    <f.icon size={18} strokeWidth={1.6} data-id="element-1948" data-mp="34322b1tbs17o" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1949" data-mp="2za0hyeacn9gp">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-1950" data-mp="3d49xhb0jolxj">
                    {f.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Access & Scholarships */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1951" data-mp="3i2l7blquawfr">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1952" data-mp="1sndd03slmq3p" />
        <Container data-id="element-1953" data-mp="1nn8u6747xjab">
          <h3 className="font-display text-lg text-ink-900" data-id="element-1954" data-mp="1nnschoxs69ov">
            Access & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1955" data-mp="34ycyk2aa3b9">
            Some cohorts may include limited scholarship places through
            community partnerships to ensure our programs remain accessible to
            motivated learners.
          </p>
        </Container>
      </section>

      {/* Educational disclaimer */}
      <section className="py-10 md:py-14 bg-white/60" data-id="element-1956" data-mp="3uib60cia7fjh">
        <Container data-id="element-1957" data-mp="3gjl7qtjq79tj">
          <aside aria-labelledby="core-courses-disclaimer" className="rounded-2xl border border-[#E0BFB3]/60 bg-[#F7EDE9] px-6 py-5 md:px-8 md:py-6" data-id="element-1958" data-mp="3gj1nv7aimbcv">
            <h2 id="core-courses-disclaimer" className="font-display text-lg text-ink-900" data-id="element-1959" data-mp="36mqa0cva1dgb">
              Disclaimer
            </h2>
            <p className="mt-3 max-w-5xl text-sm leading-relaxed text-ink-700" data-id="element-1960" data-mp="14uk6590ldkco">
              Our programs provide general, applied educational content.
            </p>
            <ul className="mt-4 max-w-5xl list-disc space-y-3 pl-5 text-sm leading-relaxed text-ink-700 marker:text-ink-900" data-id="element-1961" data-mp="19svfzjqvzuuw">
              <li className="pl-1" data-id="element-1962" data-mp="30xox6njshym6">
                <strong className="text-ink-900" data-id="element-1963" data-mp="30yrnoud79z1k">
                  Personal &amp; Family Matters:
                </strong>{' '}
                For specific legal or personal rulings (<em data-id="element-1964" data-mp="2rxaapkv3adh3">fatwas</em>)
                regarding marriage (<em data-id="element-1965" data-mp="2rwidk5lug7op">nikah</em>), divorce, or inheritance,
                please consult a qualified Islamic scholar or a recognized
                Sharia council. We are glad to help facilitate a connection to
                trusted scholarly authorities upon request.
              </li>
              <li className="pl-1" data-id="element-1966" data-mp="2d8xeqnw04uml">
                <strong className="text-ink-900" data-id="element-1967" data-mp="2d5rmizs2tcmn">
                  Business &amp; Finance:
                </strong>{' '}
                Professional, financial, and business-related content is
                strictly educational and practical in nature and does not
                constitute regulated legal, tax, or financial advice.
              </li>
            </ul>
          </aside>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-1968" data-mp="jolxxxbd2gip">
        <Container data-id="element-1969" data-mp="jr86o09zn22i">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-1970" data-mp="oorkiz2rxctx">
            <div className="max-w-xl" data-id="element-1971" data-mp="sptjrfwoxovw">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1972" data-mp="sos8babrj0dn">
                Start with a Core Course
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-1973" data-mp="yx82imd9qa7">
                Choose a track, request details, and join the next cohort.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1974" data-mp="4yxxeyb07lw1">
              <Link to="/en/waitlist" data-id="element-1975" data-mp="9qz6kbcvkwhu">
                <Button variant="primary" data-id="element-1976" data-mp="9s0s59rip1dt">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-1977" data-mp="39fikk9e7j1i2">
                <Button variant="secondary" data-id="element-1978" data-mp="39egw9386kiie">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}